import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { useLocation } from "react-router-dom";
import {
  deleteFBLead,
  getAllStudentLeads,
} from "./services/integration.services";
import {
  clearFBLeadID,
  clearStudentLeadList,
  setFBLeadID,
} from "./integrationSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import AlertBox from "../../components/common/AlertBox";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete, MdOutlineRemoveRedEye } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Button } from "@progress/kendo-react-buttons";
import { checkAcessRights } from "../../_helper/helper";
import { EXCEL_EXPORT_ACCESS } from "../../_contstants/common";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import moment from "moment";

const LeadCenterList: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const FormID = location?.state?.FormID;
  const isExcelExportAccess = checkAcessRights(
    "/integrations",
    EXCEL_EXPORT_ACCESS,
  );
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  useEffect(() => {
    if (FormID) {
      dispatch(getAllStudentLeads(FormID));
    }

    return () => {
      dispatch(clearStudentLeadList());
    };
  }, []);

  return (
    <GridLayout
      cols={[{ width: "100%" }]}
      style={{ height: "100%", margin: "4px 10px" }}
    >
      <GridLayoutItem
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px 0px 15px 0",
          height: "80px",
        }}
      >
        <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
          FB/Insta Lead List
        </Typography.h4>
        {isExcelExportAccess && (
          <Button
            type="button"
            fillMode={"solid"}
            themeColor={"primary"}
            onClick={excelExport}
          >
            Excel Export
          </Button>
        )}
      </GridLayoutItem>
      <GridLayoutItem>
        <LeadGridComponent ref={_export} />
      </GridLayoutItem>
    </GridLayout>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const LeadGridComponent = ({ ref }: { ref: any }) => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const StudentLeadList = useAppSelector(
    (state: RootState) => state.integration.StudentLeadList,
  );
  const loading = useAppSelector(
    (state: RootState) => state.integration.loading,
  );

  const [uiLoading, setLoading] = useState<boolean>(false);
  const [isUpdated, setUpdated] = useState<boolean>(false);
  const [leadDataJsonViewData, setLeadDataJsonViewData] = useState<{
    data: any;
    adName: string;
  }>({ data: null, adName: "" });

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? StudentLeadList?.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  useEffect(() => {
    setUpdated(!isUpdated);
  }, [JSON.stringify(StudentLeadList), StudentLeadList.length]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  if (uiLoading) return <LoadingPanel gridRef={gridRef} />;

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 1 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyEditCommandCell = (props: any) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteFBLead"));
      dispatch(setFBLeadID(ID));
    };

    const handleOpenLeadDataJsonView = (data: any, adName: string) => {
      let parsedData = data;
      if (typeof data === "string") {
        try {
          parsedData = JSON.parse(data);
        } catch {
          parsedData = null;
        }
      }
      setLeadDataJsonViewData({ data: parsedData, adName: adName || "" });
      dispatch(openDialog("leadDataJsonView"));
    };

    const hasLeadDataJson = (() => {
      const raw = props.dataItem?.LeadDataJson;
      if (!raw) return false;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return (
            parsed &&
            Array.isArray(parsed?.field_data) &&
            parsed.field_data.length > 0
          );
        } catch {
          return false;
        }
      }
      return (
        typeof raw === "object" &&
        Array.isArray(raw?.field_data) &&
        raw.field_data.length > 0
      );
    })();

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          className="color-red"
          onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          icon={MdDelete}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "24px" }}
        />
        {hasLeadDataJson ? (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View Lead Data"
            themeColor="info"
            onClick={() =>
              handleOpenLeadDataJsonView(
                props.dataItem?.LeadDataJson,
                props.dataItem?.AdName,
              )
            }
            icon={MdOutlineRemoveRedEye}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "22px", color: "#0d6efd" }}
          />
        ) : (
          <div style={{ width: 35 }}></div>
        )}
      </td>
    );
  };

  return (
    <>
      <DeleteFBLeadDialog />
      <LeadDataJsonViewDialog
        data={leadDataJsonViewData.data}
        adName={leadDataJsonViewData.adName}
      />
      {StudentLeadList && StudentLeadList?.length > 0 ? (
        <ExcelExport
          data={StudentLeadList}
          ref={ref}
          fileName={`FB_Insta_Lead_list_${moment().format("DD-MM-YYYY hh-mm A")}.xlsx`}
        >
          <KendoGrid
            style={{ maxHeight: "calc(100vh - 175px)" }}
            filterable={true}
            filter={filter}
            data={
              filter
                ? filterBy(StudentLeadList, filter)?.slice(
                    page.skip,
                    page.take + page.skip,
                  )
                : StudentLeadList?.slice(page.skip, page.take + page.skip)
            }
            onFilterChange={handleFilterChange}
            skip={page.skip}
            take={page.take}
            total={StudentLeadList?.length}
            pageable={{
              buttonCount: 5,
              pageSizes: [5, 10, 15, "All"],
              pageSizeValue: pageSizeValue,
              type: "input",
            }}
            onPageChange={pageChange}
            cells={{
              headerCell: HeaderCustomCell,
              data: MyDataCustomCell,
            }}
          >
            <Column
              field="CreatedDateTime"
              title="Date"
              cells={{
                data: (props: GridCustomCellProps) => {
                  return (
                    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
                      {props.dataItem?.CreatedDateTime}
                    </td>
                  );
                },
              }}
            />
            <Column field="FullName" title="Name" />
            <Column field="MobileNo" title="Mobile No." />
            <Column field="EmailID" title="Email ID" />
            <Column
              field=""
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          </KendoGrid>
        </ExcelExport>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteFBLeadDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const FBLeadID = useAppSelector((state) => state.integration.FBLeadID);
  const location = useLocation();
  const FormID = location?.state?.FormID;

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteFBLead(ID));
      if (response.meta.requestStatus === "fulfilled" && FormID) {
        dispatch(closeDialog());
        dispatch(clearFBLeadID());
        dispatch(getAllStudentLeads(FormID));
      }
    } else {
      if (FormID) {
        dispatch(closeDialog());
        dispatch(clearFBLeadID());
        dispatch(getAllStudentLeads(FormID));
      }
    }
  };
  return (
    <>
      {dialogName === "deleteFBLead" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete FB/Insta Lead?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Lead?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(FBLeadID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default LeadCenterList;

interface LeadDataJsonViewDialogProps {
  data: any;
  adName?: string;
}

const LeadDataJsonViewDialog: React.FC<LeadDataJsonViewDialogProps> = ({
  data,
  adName,
}) => {
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const formatFieldName = (name: string): string => {
    const cleaned = name.replace(/_/g, " ");
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const formatValue = (value: string): string => {
    let decoded = value
      .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16)),
      )
      .replace(/\u002B/g, "+")
      .replace(/\u20B9/g, "₹")
      .replace(/\u2013/g, "–");
    decoded = decoded.replace(/_/g, " ");
    return decoded;
  };

  const fieldData: { name: string; values: string[] }[] =
    data?.field_data || [];

  const createdTime = data?.created_time
    ? moment(data.created_time).format("DD MMM YYYY, hh:mm A")
    : "";

  return (
    <>
      {dialogName === "leadDataJsonView" && (
        <AppDialog>
          <>
            <Typography.h5 style={{ marginBottom: 0 }}>
              Lead Form Data{" "}
              <span className="fw-bold">{adName ? ` - (${adName})` : ""}</span>
            </Typography.h5>
            <div
              style={{
                minWidth: 560,
                maxWidth: 700,
                padding: "8px 0",
                fontFamily: "inherit",
              }}
            >
              {createdTime && (
                <p
                  style={{
                    margin: "0 0 12px",
                    fontSize: 13,
                    color: "#6c757d",
                    borderBottom: "1px solid #e9ecef",
                    paddingBottom: 8,
                  }}
                >
                  <strong>Submitted at:</strong> {createdTime}
                </p>
              )}
              <div
                style={{ overflowX: "auto", maxHeight: 450, overflowY: "auto" }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                        color: "#fff",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          width: "45%",
                          borderRight: "1px solid rgba(255,255,255,0.15)",
                        }}
                      >
                        Title
                      </th>
                      <th
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fieldData.map(
                      (
                        field: { name: string; values: string[] },
                        idx: number,
                      ) => (
                        <tr
                          key={idx}
                          style={{
                            background: idx % 2 === 0 ? "#f8f9fa" : "#ffffff",
                            borderBottom: "1px solid #e9ecef",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            ((e.currentTarget as HTMLElement).style.background =
                              "#eef4ff")
                          }
                          onMouseLeave={(e) =>
                            ((e.currentTarget as HTMLElement).style.background =
                              idx % 2 === 0 ? "#f8f9fa" : "#ffffff")
                          }
                        >
                          <td
                            style={{
                              padding: "9px 16px",
                              fontWeight: 600,
                              color: "#344563",
                              borderRight: "1px solid #e9ecef",
                              verticalAlign: "top",
                            }}
                          >
                            {formatFieldName(field.name)}
                          </td>
                          <td
                            style={{
                              padding: "9px 16px",
                              color: "#495057",
                              verticalAlign: "top",
                            }}
                          >
                            {field.values
                              .map((v: string) => formatValue(v))
                              .join(", ")}
                          </td>
                        </tr>
                      ),
                    )}
                    {fieldData.length === 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#adb5bd",
                          }}
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 16,
                }}
              >
                <DialogCloseButton themeColor="primary" fillMode="solid" />
              </div>
            </div>
          </>
        </AppDialog>
      )}
    </>
  );
};
